import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = (() => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/^http/, "ws");
})();

const CoopContext = createContext(null);

export const CoopProvider = ({ children }) => {
  const { user } = useAuth();
  const [lobby, setLobby] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [wsStatus, setWsStatus] = useState("disconnected"); // disconnected | connecting | connected
  const [incomingInvites, setIncomingInvites] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const desiredCodeRef = useRef(null);
  // Ephemeral real-time positions of OTHER players: { user_id -> { wx, wy, facing, moving, nodeId, classeId, name, lastUpdate } }
  // Stored in a ref (NOT state) so the canvas can read it every frame without triggering re-renders.
  const remotePlayersRef = useRef({});

  // ----- HTTP helpers -----
  const refreshMyLobby = useCallback(async () => {
    if (!user) { setLobby(null); return null; }
    try {
      const res = await axios.get(`${API}/lobby/my/current`, { withCredentials: true });
      setLobby(res.data.lobby || null);
      return res.data.lobby || null;
    } catch (e) {
      return null;
    }
  }, [user]);

  const fetchIncomingInvites = useCallback(async () => {
    if (!user) { setIncomingInvites([]); return; }
    try {
      const res = await axios.get(`${API}/lobby/invites/incoming`, { withCredentials: true });
      setIncomingInvites(res.data.invites || []);
    } catch (e) {}
  }, [user]);

  useEffect(() => {
    refreshMyLobby();
    fetchIncomingInvites();
  }, [refreshMyLobby, fetchIncomingInvites]);

  // Periodically poll incoming invites (every 20s)
  useEffect(() => {
    if (!user) return;
    const t = setInterval(fetchIncomingInvites, 20000);
    return () => clearInterval(t);
  }, [user, fetchIncomingInvites]);

  const createLobby = useCallback(async (name) => {
    const res = await axios.post(`${API}/lobby/create`, { name }, { withCredentials: true });
    setLobby(res.data);
    return res.data;
  }, []);

  const joinLobby = useCallback(async (code) => {
    const res = await axios.post(`${API}/lobby/join`, { code: code.toUpperCase() }, { withCredentials: true });
    setLobby(res.data);
    return res.data;
  }, []);

  const leaveLobby = useCallback(async () => {
    if (wsRef.current) {
      try { wsRef.current.close(1000); } catch (e) {}
      wsRef.current = null;
    }
    desiredCodeRef.current = null;
    setWsStatus("disconnected");
    try { await axios.post(`${API}/lobby/leave`, {}, { withCredentials: true }); } catch (e) {}
    setLobby(null);
    setChatMessages([]);
  }, []);

  const inviteFriend = useCallback(async (friend_user_id) => {
    const res = await axios.post(`${API}/lobby/invite`, { friend_user_id }, { withCredentials: true });
    return res.data;
  }, []);

  const declineInvite = useCallback(async (invite_id) => {
    await axios.post(`${API}/lobby/invites/${invite_id}/decline`, {}, { withCredentials: true });
    setIncomingInvites((cur) => cur.filter((i) => i.invite_id !== invite_id));
  }, []);

  const acceptInvite = useCallback(async (invite) => {
    const joined = await joinLobby(invite.code);
    setIncomingInvites((cur) => cur.filter((i) => i.invite_id !== invite.invite_id));
    return joined;
  }, [joinLobby]);

  // ----- Session token (needed for WS auth) -----
  // We retrieve a fresh non-httpOnly token by piggybacking on /auth/me + a tiny endpoint?
  // Simplest: store sessionToken in sessionStorage on login (set in AuthCallback).
  useEffect(() => {
    const stored = window.sessionStorage.getItem("checkpoint_session_token");
    if (stored) setSessionToken(stored);
  }, [user]);

  // ----- WebSocket connection -----
  const connectWS = useCallback((code) => {
    if (!code || !sessionToken) return;
    desiredCodeRef.current = code;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    setWsStatus("connecting");
    const url = `${WS_URL}/api/ws/lobby/${code}?token=${encodeURIComponent(sessionToken)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      setWsStatus("connected");
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "lobby_state") {
          setLobby(msg.lobby);
        } else if (msg.type === "chat") {
          setChatMessages((cur) => [...cur.slice(-49), msg]);
        } else if (msg.type === "presence") {
          // Drop ephemeral position of a disconnected player
          if (msg.connected === false && msg.user_id) {
            delete remotePlayersRef.current[msg.user_id];
          }
          // Touch lobby to reflect connection map next render
          setLobby((cur) => cur ? { ...cur } : cur);
        } else if (msg.type === "player_pos") {
          // Ephemeral real-time position of another player — stored in ref, no re-render
          if (msg.user_id && msg.pos) {
            remotePlayersRef.current[msg.user_id] = { ...msg.pos, lastUpdate: Date.now() };
          }
        } else if (msg.type === "error") {
          console.warn("[coop] server error:", msg.message);
        } else if (msg.type === "state_conflict") {
          if (msg.lobby) setLobby(msg.lobby);
        }
      } catch (e) {}
    };
    ws.onclose = () => {
      setWsStatus("disconnected");
      wsRef.current = null;
      // Attempt reconnect if still desired and lobby exists
      if (desiredCodeRef.current && !reconnectTimerRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (desiredCodeRef.current) connectWS(desiredCodeRef.current);
        }, 2000);
      }
    };
    ws.onerror = () => {
      try { ws.close(); } catch (e) {}
    };
  }, [sessionToken]);

  // Auto-connect when lobby + token both available
  useEffect(() => {
    if (lobby?.code && sessionToken) {
      connectWS(lobby.code);
    }
    return () => {
      // we intentionally don't disconnect on every render; only on unmount
    };
  }, [lobby?.code, sessionToken, connectWS]);

  useEffect(() => {
    return () => {
      if (wsRef.current) try { wsRef.current.close(); } catch (e) {}
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, []);

  // ----- WS actions -----
  const wsSend = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const selectClass = useCallback((classe_id) => wsSend({ type: "select_class", classe_id }), [wsSend]);
  const startGame = useCallback((game_state) => wsSend({ type: "start_game", game_state }), [wsSend]);
  const publishState = useCallback((game_state, opts = {}) => {
    return wsSend({
      type: "state_update",
      game_state,
      advance_turn: !!opts.advanceTurn,
      expected_revision: opts.expectedRevision,
    });
  }, [wsSend]);
  const endRun = useCallback(() => wsSend({ type: "end_run" }), [wsSend]);
  const sendChat = useCallback((text) => wsSend({ type: "chat", text }), [wsSend]);
  // Ephemeral position broadcast (real-time co-op sprites). Fire-and-forget, throttled by caller.
  const sendPlayerPos = useCallback((pos) => wsSend({ type: "player_pos", pos }), [wsSend]);

  // ----- Derived flags -----
  const coopMode = !!(lobby && lobby.status === "active");
  const isHost = !!(lobby && user && lobby.host_user_id === user.user_id);
  const isMyTurn = !!(coopMode && user && lobby.current_turn_user_id === user.user_id);
  const playerCount = lobby?.members?.length || 0;

  const value = useMemo(() => ({
    lobby,
    coopMode,
    isHost,
    isMyTurn,
    playerCount,
    wsStatus,
    chatMessages,
    incomingInvites,
    refreshMyLobby,
    fetchIncomingInvites,
    createLobby,
    joinLobby,
    leaveLobby,
    inviteFriend,
    acceptInvite,
    declineInvite,
    selectClass,
    startGame,
    publishState,
    endRun,
    sendChat,
    sendPlayerPos,
    remotePlayersRef,
  }), [lobby, coopMode, isHost, isMyTurn, playerCount, wsStatus, chatMessages, incomingInvites, refreshMyLobby, fetchIncomingInvites, createLobby, joinLobby, leaveLobby, inviteFriend, acceptInvite, declineInvite, selectClass, startGame, publishState, endRun, sendChat, sendPlayerPos]);

  return <CoopContext.Provider value={value}>{children}</CoopContext.Provider>;
};

export const useCoop = () => {
  const ctx = useContext(CoopContext);
  if (!ctx) throw new Error("useCoop must be used within CoopProvider");
  return ctx;
};
